import React, { useEffect, useCallback } from 'react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { sendClickedArticleData } from '../services/ArticleRecommendationService';
import { AiClickerPostData, SystemType } from '../types/AiClicker';

const AiClicker: React.FC = () => {
  const getClickData = useCallback(async (): Promise<AiClickerPostData | null> => {
    const postId = window.Article?.id;
    if (!postId) {
      return null;
    }

    const fingerprintAgent = await FingerprintJS.load();
    const fingerprintResult = await fingerprintAgent.get();

    const urlParams = new URLSearchParams(window.location.search);
    const utmPosition = urlParams.get('utm_position');
    const utmSource = urlParams.get('utm_source') || 'Forbes';
    const utmMedium = urlParams.get('utm_medium');

    const clickData: AiClickerPostData = {
      postId: postId.toString(),
      source: {
        utm_referer: document.referrer || '',
        utm_source: utmSource,
        utm_medium: utmMedium || '',
        utm_device: window.innerWidth >= 992 ? 'desktop' : 'mobile',
        utm_system: getSystem(),
        utm_timestamp: Math.floor(Date.now() / 1000),
      },
    };

    if (utmPosition) {
      clickData.source.utm_position = parseInt(utmPosition, 10);
    }

    if (window.crm_user?.user?.id) {
      clickData.userId = window.crm_user.user.id.toString();
      clickData.userToken = fingerprintResult.visitorId;
    } else if (fingerprintResult.visitorId) {
      clickData.userToken = fingerprintResult.visitorId;
    } else {
      console.error('AiClicker: Error: No user ID or token found for click data!');
      return null;
    }

    if (!clickData.postId || (!clickData.userId && !clickData.userToken)) {
      console.log('AiClicker: Invalid click data. Skipping.');
      return null;
    }

    return clickData;
  }, []);

  const sendClickData = useCallback(async () => {
    const clickData = await getClickData();
    if (!clickData) return;

    try {
      await sendClickedArticleData(clickData);
      console.log('AiClicker: Click data sent successfully.');
    } catch (error) {
      console.error('AiClicker: Error sending click data:', error);
    }

    // Remove utm parameters from URL
    removeUtmParamsFromUrl();
  }, [getClickData]);

  useEffect(() => {
    sendClickData();
  }, [sendClickData]);

  const getSystem = (): SystemType => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (
      userAgent.includes('mac') ||
      userAgent.includes('iphone') ||
      userAgent.includes('ipad') ||
      userAgent.includes('ipod')
    ) {
      return 'apple';
    }
    if (userAgent.includes('win')) {
      return 'windows';
    }
    if (userAgent.includes('android')) {
      return 'android';
    }
    if (userAgent.includes('linux')) {
      return 'linux';
    }
    return 'windows'; // Default to windows if unable to determine
  };

  const removeUtmParamsFromUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('utm_position');
    url.searchParams.delete('utm_referer');
    url.searchParams.delete('utm_source');
    url.searchParams.delete('utm_medium');
    window.history.replaceState({}, document.title, url.toString());
  };

  return null;
};

export default AiClicker;
